import React, { useRef, useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes  } from "react-router-dom"
import { AuthenticatedTemplate, UnauthenticatedTemplate,useMsal } from "@azure/msal-react";
import { EventType } from "@azure/msal-browser";
import { ApolloProvider } from '@apollo/client';
import { Box } from '@mui/material';
import useConfirm from "hooks/useConfirm";
import globalTheme from 'themes/globalTheme';
import { tokenRequest, applicationRoles } from "config/authConfigB2C";
import { apolloClient } from 'config/apolloConfig';
// import { applicationRoles } from 'config/authConfig';
import logo from 'images/Leonardo_Logo.png';

import ApplicationHeader from 'components/application/ApplicationHeader';
import RestrictedRoute from 'components/library/routing/RestrictedRoute';
import Spinner from 'components/library/alerts/Spinner'; 
import { env } from './env';

const Login = lazy(() => import('components/pages/Login'));
const Dashboard = lazy(() => import('components/pages/Dashboard'));
const ShipmentNotificationCreate = lazy(() => import('components/pages/ShipmentNotificationCreate'));
const ShipmentNotificationEdit = lazy(() => import('components/pages/ShipmentNotificationEdit'));
const ShipmentNotificationIndex = lazy(() => import('components/pages/ShipmentNotificationIndex'));
const ShipmentNotificationSummary = lazy(() => import('components/pages/ShipmentNotificationSummary'));
const WIPIndex = lazy(() => import('components/pages/WIPIndex'));
const WIPSummary = lazy(() => import('components/pages/WIPSummary'));
const EscalationIndex = lazy(() => import('components/pages/EscalationIndex'));
const EscalationSummary = lazy(() => import('components/pages/EscalationSummary'));
const LoadStatusIndex  = lazy(() => import('components/pages/LoadStatusIndex'));
const UserAdminIndex = lazy(() => import('components/pages/UserAdminIndex'));
const UserAdminSummary = lazy(() => import('components/pages/UserAdminSummary')); 
const UserAdminEdit = lazy(() => import('components/pages/UserAdminEdit'));


const NotFound = ()=> {
    return <div>Page Not Found</div>
}

const Unauthorised = ()=> {
    return <div>You are not authorised to access this resource</div> 
}

function App(props) {  

    const headerRef = useRef();
    const { instance } = useMsal();
    const { confirm } = useConfirm(); 

    useEffect(() => {
        // Set callback to listen to msal events
        const callbackId = instance.addEventCallback((message) => {
            switch (message.eventType) {
                case EventType.ACQUIRE_TOKEN_SUCCESS: 
                    confirm({
                        title: "Important System Message!",
                        text: "Please do not enter any ITAR or other sensitive data",
                        showReject: false
                    });
                    break;
                case EventType.LOGOUT_SUCCESS:
                    apolloClient.resetStore();
                    // remove any cached filter or grid state from session storage
                    var n = sessionStorage.length;
                    while(n--) {
                        var key = sessionStorage.key(n);
                        if(/FILTER_CACHE_/.test(key) || /GRID_STATE_CACHE_/.test(key)) {
                            sessionStorage.removeItem(key);
                        }  
                    }
                    break;
                case EventType.ACQUIRE_TOKEN_FAILURE:
                    instance.loginRedirect(tokenRequest); 
                    break;
                default: 
            }
        });

        return () => {
            if (callbackId) { 
                instance.removeEventCallback(callbackId); 
            }
        }

    }, []);

    useEffect(() => {
        if (headerRef.current) {
            globalTheme.nav.height = headerRef.current.getBoundingClientRect().height; 
        }
    }, [headerRef])

    return (
        <BrowserRouter>
            <ApolloProvider client={apolloClient}>
                <AuthenticatedTemplate> 
                    <Box style={{ backgroundColor: "#fafafa" }}> 
                        <header ref={headerRef} className="no-padding">
                            <ApplicationHeader logo={logo} title="Portal Demonstrator" />
                        </header>
                        <Box ml={10} mr={10}>
                            <Suspense fallback={<Spinner />}>
                                <Routes>
                                    <Route exact path="/" element={<Dashboard />} />
                                    <Route exact path="/wips" element={<WIPIndex />} />
                                    <Route exact path="/wips/:id" element={<WIPSummary />} />
                                    <Route exact path="/notifications" element={<ShipmentNotificationIndex />} />

                                    <Route 
                                        exact path="/notifications/new" 
                                        element={
                                            <RestrictedRoute allowFor={[applicationRoles.EXTERNAL_CUSTOMER]}> 
                                                <ShipmentNotificationCreate />
                                            </RestrictedRoute>
                                        } 
                                    />

                                    <Route 
                                        exact path="/notifications/edit/:id" 
                                        element={
                                            <RestrictedRoute allowFor={[applicationRoles.EXTERNAL_CUSTOMER]}>
                                                <ShipmentNotificationEdit />
                                            </RestrictedRoute>
                                        } 
                                    />

                                    <Route exact path="/notifications/:id" element={<ShipmentNotificationSummary />} />
                                    <Route exact path="/escalations" element={<EscalationIndex />} />
                                    <Route exact path="/escalations/:id" element={<EscalationSummary />} />
                                    <Route exact path="/loadstatus/" element={<LoadStatusIndex />} />

                                    <Route 
                                        exact path="/users" 
                                        element={
                                            <RestrictedRoute allowFor={[applicationRoles.INTERNAL_CUSTOMER_CS3]}>
                                                <UserAdminIndex />
                                            </RestrictedRoute>
                                        } 
                                    />

                                    <Route 
                                        exact path="/users/:id" 
                                        element={
                                            <RestrictedRoute allowFor={[applicationRoles.INTERNAL_CUSTOMER_CS3]}>
                                                <UserAdminSummary />
                                            </RestrictedRoute>
                                        } 
                                    />

                                    <Route 
                                        exact path="/users/edit/:id" 
                                        element={
                                            <RestrictedRoute allowFor={[applicationRoles.INTERNAL_CUSTOMER_CS3]}>
                                                <UserAdminEdit />
                                            </RestrictedRoute>
                                        } 
                                    />

                                    <Route exact path="/unauthorised/" element={<Unauthorised />} />
                                    <Route path="*" element={<NotFound />} />
                                </Routes>
                            </Suspense>
                        </Box>
                    </Box>
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                    <Login isAutoRedirect={true} />
                </UnauthenticatedTemplate>
            </ApolloProvider>
        </BrowserRouter>
    );
}

export default App;